// SpeedAssign — build fingerprint in Help & Training support details.
//
// Release-Truth extension (Feb 2026): now also renders the environment
// ("production" / "preview" / "dev"), the application_version, and
// the build_time in readable, selectable text and the tooltip so a bug
// report can identify the exact build.
//
// Feb 2026 · production/preview integrity batch (DEFECT 6): the
// environment label is resolved by a deterministic HOST classifier
// (`resolveEnvLabel()`), not the build-injected env var — both
// production (speedassign.com) and preview were rendering "dev"
// because the injected `REACT_APP_ENVIRONMENT` was missing on both
// bundles.
//
// Data sources (build-injected in this order of authority):
//   * REACT_APP_BUILD_SHA         — 40-char git SHA
//   * REACT_APP_BUILD_TIMESTAMP   — ISO 8601 UTC
//   * REACT_APP_APPLICATION_VERSION — semver-ish app version
//
// The tooltip's format is stable so ops can grep against it in
// screenshots ("build <sha> · <env> · <app_version> · <ts>").
import { resolveEnvLabel } from "@/lib/envLabel";

export function BuildStamp() {
  const sha = process.env.REACT_APP_BUILD_SHA || "dev";
  const ts = process.env.REACT_APP_BUILD_TIMESTAMP || "";
  const env = resolveEnvLabel();
  const appVersion = process.env.REACT_APP_APPLICATION_VERSION || "";

  const parts = [`build ${sha}`, env];
  if (appVersion) parts.push(`v${appVersion}`);
  if (ts) parts.push(ts);
  const title = parts.join(" · ");

  // Environment badge tone: production = red, preview = amber, dev = zinc.
  const envTone =
    env === "production" ? "text-rose-500"
      : env === "preview" ? "text-amber-400"
      : "text-zinc-300";

  return (
    <dl
      data-testid="build-stamp"
      title={title}
      className="space-y-2 text-sm text-zinc-300 select-text"
    >
      <div>
        <dt className="text-xs text-zinc-400">Environment</dt>
        <dd data-testid="build-stamp-env" className={envTone}>{env}</dd>
      </div>
      <div>
        <dt className="text-xs text-zinc-400">Build</dt>
        <dd data-testid="build-stamp-sha" className="font-mono break-all">{sha}</dd>
      </div>
      {appVersion ? (
        <div>
          <dt className="text-xs text-zinc-400">Version</dt>
          <dd className="break-words">{appVersion}</dd>
        </div>
      ) : null}
      {ts ? (
        <div>
          <dt className="text-xs text-zinc-400">Built</dt>
          <dd className="font-mono break-all">{ts}</dd>
        </div>
      ) : null}
    </dl>
  );
}
